import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonRadiusClasses = "rounded-full";
const buttonEmphasisBaseClasses = `font-bold ${buttonRadiusClasses}`;
const buttonMotionClasses =
  "transition-all hover:-translate-y-0.5 active:translate-y-0";
const buttonPointerClasses =
  "disabled:pointer-events-none [&_svg]:pointer-events-none hover:cursor-pointer";
const buttonFocusClasses =
  "outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]";
const buttonAriaClasses =
  "aria-invalid:ring-destructive/20 aria-invalid:border-destructive";
const buttonLayoutClasses = `inline-flex items-center justify-center gap-2 whitespace-nowrap ${buttonRadiusClasses} text-sm font-semibold uppercase disabled:opacity-50 [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0`;

const buttonVariants = cva(
  `${buttonLayoutClasses} ${buttonMotionClasses} ${buttonPointerClasses} ${buttonFocusClasses} ${buttonAriaClasses}`,
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground normal-case shadow-sm hover:bg-primary/90 hover:shadow-md",
        destructive:
          "bg-destructive text-white shadow-sm hover:bg-destructive/90 hover:shadow-md focus-visible:ring-destructive/20",
        outline:
          "border bg-background shadow-xs hover:bg-accent hover:text-accent-foreground",
        secondary:
          "bg-accent text-accent-foreground shadow-sm hover:bg-accent/80 hover:shadow-md",
        ghost: "shadow-none hover:bg-accent hover:text-accent-foreground",
        white: `${buttonEmphasisBaseClasses} bg-white text-primary px-16 shadow-sm hover:bg-white/90 hover:shadow-md`,
        link: "rounded-none! px-0! py-0! text-base font-normal normal-case text-info-foreground underline underline-offset-[0.15em] shadow-none hover:translate-y-0 hover:bg-info-light hover:text-foreground focus-visible:bg-info-light focus-visible:text-foreground focus-visible:ring-0",
      },
      size: {
        default: "h-auto rounded-full px-5 py-2.5",
        sm: "h-8 gap-1.5 px-3 has-[>svg]:px-2.5",
        lg: "h-10 px-6 has-[>svg]:px-4",
        xl: "h-12 px-8 text-base has-[>svg]:px-6",
        "2xl":
          "h-14 px-12 text-lg has-[>svg]:px-10 [&_svg:not([class*='size-'])]:size-5",
        wide: "h-10 px-20 has-[>svg]:px-16",
        icon: "h-9 w-9 rounded-full p-0",
        "icon-sm": "h-8 w-8 rounded-full p-0",
        "icon-lg": "h-10 w-10 rounded-full p-0",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

const Button = ({
  className,
  variant,
  size,
  asChild = false,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  }) => {
  const Comp = asChild ? Slot : "button";

  const resolvedClassName = cn(buttonVariants({ variant, size, className }));

  return <Comp data-slot="button" className={resolvedClassName} {...props} />;
};

export { Button, buttonVariants };
